var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { MDS_COLORS_LINE_BORDER } from '@rappi/mustache-b2b-foundations';
import { BaseButton, Button, CrossOutlineIcon, FlexContainer, Typography } from '@rappi/rests-commons-front';
import { lazy, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSWRConfig } from 'swr';
import { FileUploader } from '../../commons/menu-file-uploader/file-uploader';
import { countries } from '../../constants/countries';
import { useGlobal } from '../../context/global/global.context';
import { GlobalActions } from '../../context/global/types';
import { useCountriesAllowed } from '../../hooks/use-countries-allowed';
import { menuGeneratorService } from '../../services/menu-generator.service';
import { observabilityService } from '../../services/observability';
const Select = lazy(() => import(/* webpackChunkName: `Select` */ 'storesetupapp/components/Select'));
const Input = lazy(() => import(/* webpackChunkName: `Input` */ 'storesetupapp/components/Input'));
export const MenuGeneratorSidebar = ({ mutateData, onClose }) => {
    const { state: { auth, country: defaultCountry }, dispatch, } = useGlobal();
    const { t } = useTranslation();
    const { mutate: mutateGlobal } = useSWRConfig();
    const { data: countriesAllowed } = useCountriesAllowed();
    const [restaurantName, setRestaurantName] = useState('');
    const [leadId, setLeadId] = useState('');
    const [menuFile, setMenuFile] = useState(null);
    const [menuFileError, setMenuFileError] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [country, setCountry] = useState(defaultCountry !== null && defaultCountry !== void 0 ? defaultCountry : null);
    const resetForm = () => {
        setRestaurantName('');
        setLeadId('');
        setMenuFile(null);
        setMenuFileError('');
    };
    const handleFileChange = (file) => {
        setMenuFile(file);
    };
    const handleErrorFile = (errorMessage) => {
        setMenuFileError(errorMessage);
    };
    const uploadMenuFile = (file) => __awaiter(void 0, void 0, void 0, function* () {
        try {
            return yield menuGeneratorService.uploadFile(file);
        }
        catch (error) {
            observabilityService.captureError({
                error,
                name: 'upload-menu-file',
                user: auth === null || auth === void 0 ? void 0 : auth.userProfile,
            });
            throw error;
        }
    });
    const generateMenu = (hunterName, restaurantName, leadId, menuFileURL, country) => __awaiter(void 0, void 0, void 0, function* () {
        try {
            yield menuGeneratorService.generateMenu({ hunterName, restaurantName, leadId, country, fileURL: menuFileURL });
        }
        catch (error) {
            observabilityService.captureError({
                error,
                name: 'generate-menu',
                user: auth === null || auth === void 0 ? void 0 : auth.userProfile,
            });
            throw error;
        }
    });
    const handleGenerateMenu = () => __awaiter(void 0, void 0, void 0, function* () {
        var _a, _b;
        try {
            setIsLoading(true);
            if (menuFile === null)
                throw new Error('No file selected');
            if (country === null)
                throw new Error('No country selected');
            const menuFileURL = yield uploadMenuFile(menuFile);
            yield generateMenu((_b = (_a = auth === null || auth === void 0 ? void 0 : auth.userProfile) === null || _a === void 0 ? void 0 : _a.name) !== null && _b !== void 0 ? _b : '', restaurantName, leadId, menuFileURL, country);
            yield mutateData();
            yield mutateGlobal(`top-hunter/${country}`);
            yield mutateGlobal(`own-stats/${country}`);
            resetForm();
            dispatch({
                type: GlobalActions.showNotification,
                payload: {
                    message: t('menuGenerator.sidebar.form.notification.success', { restaurantName }),
                    type: 'success',
                },
            });
        }
        catch (_c) {
            dispatch({
                type: GlobalActions.showNotification,
                payload: { message: t('menuGenerator.sidebar.form.notification.error', { restaurantName }), type: 'error' },
            });
        }
        finally {
            setIsLoading(false);
            onClose();
        }
    });
    return (_jsxs(FlexContainer, { height: "100%", direction: "column", children: [_jsxs(FlexContainer, { justifyContent: "space-between", padding: "16px 32px", alignItems: "center", children: [_jsx(Typography, { component: "subheadline", semibold: true, children: t('menuGenerator.sidebar.form.title') }), _jsx(BaseButton, { onClick: onClose, children: _jsx(CrossOutlineIcon, {}) })] }), _jsxs(FlexContainer, { height: "100%", direction: "column", padding: "32px", gap: 24, style: { borderWidth: '1px 0', borderStyle: 'solid', borderColor: MDS_COLORS_LINE_BORDER }, children: [_jsx(Input, { fullWidth: true, name: "restaurantName", label: t('menuGenerator.sidebar.form.restaurantName'), value: restaurantName, onChange: (e) => {
                            setRestaurantName(e.target.value);
                        } }), _jsx(Input, { fullWidth: true, name: "leadId", label: t('menuGenerator.sidebar.form.leadId'), value: leadId, onChange: (e) => {
                            setLeadId(e.target.value);
                        } }), countriesAllowed && (_jsx(Select, { fullWidth: true, name: "country", value: country !== null && country !== void 0 ? country : '', options: countries.map((c) => ({ value: c.code, label: c.name })).filter((c) => countriesAllowed[c.value]), label: t('menuGenerator.sidebar.form.country'), setValue: (selected) => {
                            setCountry(selected);
                        } })), _jsx(FileUploader, { file: menuFile, onFileUpload: handleFileChange, onError: handleErrorFile, rules: {
                            maxSizeMb: 10,
                            allowedTypes: ['image/jpg', 'application/pdf', 'image/png', 'image/jpeg', 'image/heic'],
                        } })] }), _jsx(FlexContainer, { padding: "24px 32px", justifyContent: "flex-end", children: _jsx(Button, { disabled: !restaurantName || !leadId || !menuFile || Boolean(menuFileError) || isLoading || !country, 
                    // eslint-disable-next-line @typescript-eslint/no-misused-promises
                    onClick: () => __awaiter(void 0, void 0, void 0, function* () {
                        yield handleGenerateMenu();
                    }), loading: isLoading, text: t('menuGenerator.sidebar.form.submit') }) })] }));
};
